import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  PageHeader,
  Card,
  DataTable,
  SearchInput,
  ConfirmDialog,
} from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";

const PLACEMENT_OPTIONS = [
  { value: "global", label: "Dashboard" },
  { value: "footer", label: "Global Footer" },
];

const PLACEMENT_SIZE = {
  global: "600 × 800 px (4:5)",
  footer: "728 × 160 px",
};

const placementColor = (placement, isDark) => {
  const map = {
    global: {
      bg: isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)",
      color: isDark ? "#a5b4fc" : "#6366f1",
    },
    footer: {
      bg: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)",
      color: isDark ? "#fbbf24" : "#d97706",
    },
  };
  return map[placement] || { bg: isDark ? "rgba(255,255,255,0.08)" : "#f1f5f9", color: isDark ? "#cbd5e1" : "#64748b" };
};

const orgTypeLabel = (value) => {
  if (value === "paid") return "Paid-Org";
  if (value === "sub") return "Club";
  return "All";
};

const Advertisement = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const PAGE_LIMIT = 10;
  const token = props?.props?.profile?.token;

  const [adList, setAdList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [totalAds, setTotalAds] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    action: null,
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchQuery), 600);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    if (token) fetchAds();
  }, [token, page, debouncedSearch]);

  const fetchAds = async () => {
    if (!token) return;
    setLoading(true);
    const body = {
      type: "advertisement",
      pagination: true,
      page: page + 1,
      limit: PAGE_LIMIT,
      search: debouncedSearch,
    };
    try {
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.ADVERTISEMENT_LIST,
        JSON.stringify(body),
        { "Content-Type": "application/json", "x-access-token": token }
      );
      if (res?.status) {
        setAdList(res.data || []);
        setTotalAds(res.count || 0);
      } else {
        setAdList([]);
        setTotalAds(0);
        toast.error(res?.message || "Failed to fetch advertisements");
      }
    } catch {
      toast.error("Failed to fetch advertisements");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (ad) => {
    const adLabel = ad.url_link ? `"${ad.url_link}"` : "this advertisement";
    setConfirmDialog({
      open: true,
      title: "Delete Advertisement?",
      message: `Are you sure you want to delete the ad for ${adLabel}? This cannot be undone.`,
      action: async () => {
        try {
          const res = await ApiServices.PostApiCall(
            ApiEndpoint.DELETE_ADVERTISEMENT,
            JSON.stringify({ id: ad.id }),
            { "Content-Type": "application/json", "x-access-token": token }
          );
          if (res?.status) {
            toast.success("Advertisement deleted");
            fetchAds();
          } else {
            toast.error(res?.message || "Failed to delete");
          }
        } catch {
          toast.error("Failed to delete advertisement");
        }
      },
    });
  };

  const columns = [
    {
      field: "placement",
      label: "Placement",
      render: (value) => {
        if (!value) return <Typography sx={{ color: isDark ? "#718096" : "#94a3b8", fontSize: "0.875rem" }}>—</Typography>;
        const label = PLACEMENT_OPTIONS.find((o) => o.value === value)?.label || value;
        const colors = placementColor(value, isDark);
        return (
          <Box>
            <Chip
              label={label}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: "6px",
              }}
            />
            {PLACEMENT_SIZE[value] && (
              <Typography sx={{ fontSize: "0.7rem", color: isDark ? "#94a3b8" : "#64748b", mt: 0.25 }}>
                {PLACEMENT_SIZE[value]}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "url_link",
      label: "Website Link",
      render: (value) => (
        <Typography
          sx={{
            fontSize: "0.875rem",
            color: isDark ? "#60a5fa" : "#1976d2",
            ...(isMobile
              ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "220px" }
              : { wordBreak: "break-all", maxWidth: 200 }),
          }}
        >
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "rotation_duration",
      label: "Duration",
      width: 100,
      mobileSameLineGroup: "meta",
      render: (value) => (
        <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#e2e8f0" : "#334155" }}>
          {value ? `${value}s` : "—"}
        </Typography>
      ),
    },
    {
      field: "org_type",
      label: "Org Type",
      mobileSameLineGroup: "meta",
      render: (value) => (
        <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#e2e8f0" : "#334155" }}>
          {orgTypeLabel(value)}
        </Typography>
      ),
    },
  ];

  const renderActions = (row) => (
    <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={() => navigate(`/edit-advertisement/${row.id}`)}
          sx={{
            color: isDark ? "#818cf8" : "#1976d2",
            "&:hover": { backgroundColor: isDark ? "rgba(99,102,241,0.1)" : "rgba(25,118,210,0.1)" },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          size="small"
          onClick={() => handleDelete(row)}
          sx={{
            color: isDark ? "#f87171" : "#dc2626",
            "&:hover": { backgroundColor: isDark ? "rgba(239,68,68,0.1)" : "rgba(220,38,38,0.1)" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      <PageHeader
        title="Advertisements"
        subtitle="Manage ads shown across the website"
        breadcrumbs={[{ label: "Admin" }, { label: "Advertisements" }]}
        actionLabel="Add Advertisement"
        actionIcon={<AddIcon />}
        onAction={() => navigate("/add-advertisement")}
      >
        <SearchInput
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          onClear={() => { setSearchQuery(""); setPage(0); }}
          placeholder="Search ads..."
          width={240}
        />
      </PageHeader>

      <Card noPadding>
        <DataTable
          columns={columns}
          data={adList}
          loading={loading}
          actions={renderActions}
          pagination
          page={page}
          rowsPerPage={PAGE_LIMIT}
          totalCount={totalAds}
          onPageChange={(e, newPage) => setPage(newPage)}
          emptyMessage="No advertisements found"
          mobileTitleColumn="placement"
        />
      </Card>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={async () => {
          if (confirmDialog.action) await confirmDialog.action();
          setConfirmDialog({ open: false, title: "", message: "", action: null });
        }}
        onCancel={() => setConfirmDialog({ open: false, title: "", message: "", action: null })}
      />
    </Box>
  );
};

export default Advertisement;
